import type { MouseEventHandler } from 'react';
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  Avatar,
  AvatarGroup,
  Box,
  Center,
  CloseButton,
  Dialog,
  Flex,
  FormatNumber,
  Icon,
  Stack,
  Tabs,
  Text,
} from '@chakra-ui/react';
import {
  type Msg,
  StickerSetType,
  useAppStore,
  type MessageEmojiReaction,
  StateEnum,
} from '@halo/chat-sdk-react';
import { Emoji as EmojiComponent, AvatarPure, DefaultAvatarIcon } from '@halo/ui-chat-react';
import dayjs from 'dayjs';
import { EmojiSvgRender } from '@halo/ui-chat-react';
import { Portal } from '@halo/ui-react';
import ReactedIcon from '@/assets/images/message/svg/messageReacted.svg?react';
import MoreIcon from '@/assets/images/common/svg/more.svg?react';
import { useStaticAppStore } from '@/store/client';

// Type definition
interface ReactionUser {
  user_id: string;
  emoji_id: string;
  operation_at?: number;
}

// Emoji component with onClick support
const Emoji = () => {};

// Format emoji ID to emoji character (moved up for use in pre-processing)
function formatEmojiId(code: string): string {
  try {
    // Split by underscore or space
    const codes = code.includes('_') ? code.split('_') : code.split(' ');

    return codes.map((c) => String.fromCodePoint(parseInt(c.replace('U+', ''), 16))).join('');
  } catch {
    // console.error('Failed to format emoji ID:', error);
    return '';
  }
}

const sortReactions = (reactions?: MessageEmojiReaction[], user_id?: string) => {
  const filteredReactions = (reactions || []).filter((item) => item.reaction_log.length > 0);

  // Purpose: Sort reactions by user's own reaction first, then by reaction count
  // Reason: Show user's own reactions at the top, then sort by popularity
  return filteredReactions
    .map((item) => ({
      ...item,
      isActive: item.reaction_log.some((log) => log.user_id === user_id),
    }))
    .sort((a, b) => {
      // First, sort by whether user has reacted (user's reactions first)
      if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;

      // Then, sort by reaction count (more reactions first)
      if (b.reaction_log.length !== a.reaction_log.length) {
        return b.reaction_log.length - a.reaction_log.length;
      }

      // TODO use early time compare ,not emoji_id
      return a.emoji_id.localeCompare(b.emoji_id);
    });
};

// Update Panel to use pre-processed groups

export interface EmojiPanelProps {
  onSelect: (emoji: string) => void;
}

const Panel = memo(function Panel({ onSelect }: EmojiPanelProps) {
  return <EmojiComponent.MiniPanel onSelect={onSelect} defaultTab={StickerSetType.Emoji} />;
});

const QUICK_DATA: Array<{ emoji_id: string; icon: string }> = [
  { emoji_id: '1f44c', icon: '1F44C' },
  { emoji_id: '1f44d', icon: '1F44D' },
  { emoji_id: '1f44e', icon: '1F44E' },
  { emoji_id: '2764_fe0f', icon: '2764_FE0F' },
  { emoji_id: '1f525', icon: '1F525' },
  { emoji_id: '1f603', icon: '1F603' },
  { emoji_id: '1f923', icon: '1F923' },
];

export interface EmojiQuickProps {
  onMore?: MouseEventHandler<HTMLDivElement>;
  onSelect: (emoji: string) => void;
}

const Quick = memo(function Quick({ onMore, onSelect }: EmojiQuickProps) {
  return (
    <Flex alignItems="center" px="2" h="10" gap="0.5">
      {QUICK_DATA.map(({ emoji_id, icon }) => {
        return (
          <Box
            key={emoji_id}
            _hover={{ bg: 'bg.muted' }}
            cursor="pointer"
            onClick={() => onSelect?.(emoji_id)}
            boxSize="8"
            p="0.5"
            borderRadius="md"
          >
            <EmojiSvgRender size="100%" emoji={formatEmojiId(icon)} />
          </Box>
        );
      })}
      <Center
        borderRadius="full"
        transform="rotate(90deg)"
        bg="gray.200"
        cursor="pointer"
        onClick={onMore}
        boxSize="7"
        mx="0.5"
      >
        <Icon>
          <MoreIcon />
        </Icon>
      </Center>
    </Flex>
  );
});

// Format reaction time
const formatReactionTime = (time: number) => {
  const target = dayjs(time);
  const now = dayjs();

  if (now.isSame(target, 'day')) {
    return `Today at ${target.format('HH:mm')}`;
  }
  if (now.subtract(1, 'day').isSame(target, 'day')) {
    return `Yesterday at ${target.format('HH:mm')}`;
  }
  if (now.isSame(target, 'week')) {
    return target.format('dddd at HH:mm');
  }
  if (now.subtract(1, 'week').isSame(target, 'week')) {
    return target.format('dddd at HH:mm');
  }
  if (now.isSame(target, 'year')) {
    return target.format('MM/DD at HH:mm');
  }
  return target.format('YYYY/MM/DD at HH:mm');
};

// MessageEmojiAvatar component
const MessageEmojiAvatar = memo(function MessageEmojiAvatar({
  user_id,
  boxSize = '12px',
  index = 0,
}: {
  user_id: string;
  boxSize?: `${number}px`;
  index?: number;
}) {
  const client = useAppStore((state) => state.client);
  const [avatar, setAvatar] = useState<string>();

  useEffect(() => {
    if (client && user_id) {
      const { removeListener } = client.Api.user.resolveUsersInfo(user_id, (res) => {
        if (!res) {
          setAvatar(DefaultAvatarIcon);
          return;
        }
        if (res.state === StateEnum.DELETE_ACCOUNT) {
          setAvatar(StateEnum.DELETE_ACCOUNT.toString());
        } else {
          setAvatar(res.avatar);
        }
      });
      return removeListener;
    } else {
      setAvatar(DefaultAvatarIcon);
    }
  }, [user_id, client]);

  if (!avatar) return null;

  return <AvatarPure src={avatar} boxSize={boxSize} zIndex={index} />;
});

// EmojiAvatarGroup component
interface EmojiAvatarGroupProps {
  user_ids: string[];
  showCount?: boolean;
  maxDisplay?: number;
  showMini: boolean;
  boxSize?: `${number}px`;
  user_id?: string;
}

const EmojiAvatarGroup = memo(function EmojiAvatarGroup({
  user_ids,
  user_id,
  showMini = false,
  maxDisplay = 4,
  boxSize = '12px',
}: EmojiAvatarGroupProps) {
  // If showCount is true and count > maxDisplay, only show count badge
  if (showMini) {
    return (
      <Avatar.Root
        variant="solid"
        boxSize={`calc(${boxSize} + var(--text-scale-factor, 0px))`}
        bg="transparent"
        color="brand.solid"
      >
        <Avatar.Fallback fontSize="xs" fontWeight="semibold">
          {user_ids.length}
        </Avatar.Fallback>
      </Avatar.Root>
    );
  }

  // Otherwise, show avatars up to maxDisplay
  const displayUsers = user_ids.filter((id) => id !== user_id).slice(0, maxDisplay);
  if (user_id && user_ids.includes(user_id)) displayUsers.unshift(user_id);

  return (
    <AvatarGroup gap="0" spaceX="-2" flex="none">
      {displayUsers.map((user_id, index) => (
        <MessageEmojiAvatar
          key={user_id}
          user_id={user_id}
          boxSize={boxSize}
          index={displayUsers.length - index}
        />
      ))}
    </AvatarGroup>
  );
});

function MessageMeta({ chat_id, message }: { chat_id: string; message: Msg }) {
  const { user, client } = useStaticAppStore();

  const handleClick = useCallback(
    async (emoji_id: string, op_type: 1 | 2) => {
      if (!client || !chat_id) return;
      //if message has emoji reaction, return
      await client.Api.messages.editEmojiReaction(
        chat_id,
        message.message_id,
        emoji_id,
        op_type,
        {
          client_msg_id: message.cli_msg_id,
          user_id: user.user_id,
        }
      );
    },
    [client, chat_id, message.message_id, message.cli_msg_id, user.user_id]
  );

  const sortedReactions = useMemo(
    () => sortReactions(message.emoji_reaction, user.user_id),
    [message.emoji_reaction, user.user_id]
  );

  const MAX_DISPLAY = 4;
  const totalCount = sortedReactions.reduce(
    (sum, reaction) => sum + reaction.reaction_log.length,
    0
  );

  const showMini = totalCount > MAX_DISPLAY;

  return (
    <>
      {sortedReactions.map((reaction) => {
        const user_ids = reaction.reaction_log.map((item) => item.user_id);
        const isActive = user.user_id && user_ids.includes(user.user_id);
        return (
          <Box
            key={reaction.emoji_id}
            bg={isActive ? 'brand.50' : 'brand.10'}
            borderRadius="full"
            px="1.5"
            py="1"
            display="flex"
            alignItems="center"
            gap="1"
            cursor="pointer"
            onClick={() => handleClick(reaction.emoji_id, isActive ? 2 : 1)}
          >
            <Text fontSize="sm" flexShrink={0}>
              {formatEmojiId(reaction.emoji_id)}
            </Text>
            <EmojiAvatarGroup
              user_id={user.user_id}
              showMini={showMini}
              user_ids={user_ids}
              boxSize="20px"
              maxDisplay={MAX_DISPLAY}
              showCount={true}
            />
          </Box>
        );
      })}
    </>
  );
}

// Item component for displaying reaction users
const Item = memo(function Item({ data }: { data: ReactionUser }) {
  const { client } = useAppStore();
  const [name, setName] = useState<string>('');

  useEffect(() => {
    if (client && data?.user_id) {
      const { removeListener } = client.Api.user.resolveUsersInfo(data.user_id, (res) => {
        setName(res?.name);
      });
      return removeListener;
    }
  }, [data?.user_id, client]);

  return (
    <Flex alignItems="center" gap="3" p="2">
      <MessageEmojiAvatar user_id={data.user_id} boxSize="36px" />
      <Box flex="1" minW="0">
        <Box
          fontSize="sm"
          fontWeight="medium"
          css={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {name}
        </Box>
        <Flex mt="0.5" gap="0.5" color="fg.muted" alignItems="center">
          <Icon fontSize="xs">
            <ReactedIcon />
          </Icon>
          <Text fontSize="xs">{data.operation_at && formatReactionTime(data.operation_at)}</Text>
        </Flex>
      </Box>
      <Text fontSize="lg">{formatEmojiId(data.emoji_id)}</Text>
    </Flex>
  );
});

// DetailDialog component with forwardRef
interface DetailDialogRef {
  open: (data: MessageEmojiReaction[]) => void;
}

const DetailDialog = forwardRef<DetailDialogRef>(function DetailDialog(_, ref) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<MessageEmojiReaction[]>([]);

  useImperativeHandle(ref, () => ({
    open: (newData: MessageEmojiReaction[]) => {
      setData(newData.filter((item) => item.reaction_log.length > 0));
      setOpen(true);
    },
  }));

  const handleOpenChange = useCallback((details: { open: boolean }) => {
    setOpen(details.open);
  }, []);

  // Group emojis by emoji_id
  const emojiGroups = useMemo(() => {
    const groups: Record<string, ReactionUser[]> = {};
    data.forEach((item) => {
      if (!groups[item.emoji_id]) groups[item.emoji_id] = [];

      item.reaction_log
        .sort((a, b) => b.operation_at - a.operation_at)
        .forEach((log) => {
          groups[item.emoji_id].push({
            user_id: log.user_id,
            emoji_id: item.emoji_id,
            operation_at: log.operation_at,
          });
        });
    });
    return Object.entries(groups).map(([emoji_id, users]) => ({
      emoji_id,
      users,
    }));
  }, [data]);

  const all = useMemo(
    () =>
      emojiGroups
        .map((group) =>
          group.users.map(({ user_id, emoji_id, operation_at }) => ({
            user_id,
            emoji_id,
            operation_at,
          }))
        )
        .flat()
        .sort((a, b) => (b.operation_at ?? 0) - (a.operation_at ?? 0)),
    [emojiGroups]
  );

  const totalCount = all.length;

  const [selectedEmoji, setSelectedEmoji] = useState<string>('all');

  useEffect(() => {
    if (emojiGroups.length > 0 && selectedEmoji === 'all') {
      // Keep 'all' selected
    } else if (emojiGroups.length > 0 && !emojiGroups.find((g) => g.emoji_id === selectedEmoji)) {
      setSelectedEmoji('all');
    }
  }, [emojiGroups, selectedEmoji]);

  const handleValueChange = useCallback((e: { value: string }) => {
    setSelectedEmoji(e.value);
  }, []);

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange} lazyMount placement="center">
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content w="600px" maxW="100%">
            <Dialog.Header pt="5" px="5" pb="2.5" justifyContent="space-between">
              <Dialog.Title fontSize="md" fontWeight="semibold">
                {t('chat.reacted')}
              </Dialog.Title>
              <Center cursor="pointer" onClick={() => setOpen(false)}>
                <CloseButton />
              </Center>
            </Dialog.Header>
            <Dialog.Body p="0">
              <Tabs.Root value={selectedEmoji} onValueChange={handleValueChange} variant="plain">
                <Tabs.List
                  w="full"
                  px="4"
                  py="2"
                  overflowX="auto"
                  overflowY="hidden"
                  display="flex"
                  flexWrap="wrap"
                  flexShrink={0}
                  gap="2"
                  borderBottom="1px solid"
                  borderColor="border.subtle"
                >
                  <Tabs.Trigger
                    value="all"
                    px="3"
                    py="1.5"
                    borderRadius="full"
                    fontSize="sm"
                    fontWeight="medium"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap="1"
                    bg="brand.subtle"
                    color="brand.solid"
                    _selected={{
                      bg: 'brand.solid',
                      color: 'white',
                    }}
                  >
                    <Text>{t('chat.all')}</Text>
                    <Text>
                      <FormatNumber
                        value={totalCount}
                        notation="compact"
                        compactDisplay="short"
                      />
                    </Text>
                  </Tabs.Trigger>
                  {emojiGroups.map((group) => (
                    <Tabs.Trigger
                      key={group.emoji_id}
                      value={group.emoji_id}
                      px="3"
                      py="1.5"
                      borderRadius="full"
                      fontSize="sm"
                      fontWeight="medium"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap="1"
                      bg="brand.subtle"
                      color="brand.solid"
                      _selected={{
                        bg: 'brand.solid',
                        color: 'white',
                      }}
                    >
                      <Text>{formatEmojiId(group.emoji_id)}</Text>
                      <Text>{group.users.length}</Text>
                    </Tabs.Trigger>
                  ))}
                </Tabs.List>
                <Box h="400px" position="relative" overflow="hidden" flexShrink={0}>
                  <Tabs.Content
                    value="all"
                    p="4"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      opacity: selectedEmoji === 'all' ? 1 : 0,
                      transition: 'opacity 0.15s ease-in-out',
                      pointerEvents: selectedEmoji === 'all' ? 'auto' : 'none',
                    }}
                  >
                    <Stack gap="2" h="full" overflowY="auto" className="scrollHover" pr="2">
                      {all.map(({ emoji_id, user_id, operation_at }) => (
                        <Item
                          key={`${emoji_id}-${user_id}`}
                          data={{ emoji_id, user_id, operation_at }}
                        />
                      ))}
                    </Stack>
                  </Tabs.Content>
                  {emojiGroups.map((group) => (
                    <Tabs.Content
                      key={group.emoji_id}
                      value={group.emoji_id}
                      p="4"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        opacity: selectedEmoji === group.emoji_id ? 1 : 0,
                        transition: 'opacity 0.15s ease-in-out',
                        pointerEvents: selectedEmoji === group.emoji_id ? 'auto' : 'none',
                      }}
                    >
                      <Stack gap="2" h="full" overflowY="auto" className="scrollHover" pr="2">
                        {group.users.map((user) => (
                          <Item key={`${group.emoji_id}-${user.user_id}`} data={user} />
                        ))}
                      </Stack>
                    </Tabs.Content>
                  ))}
                </Box>
              </Tabs.Root>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
});

const MessagePrefix = memo<{
  boxSize?: `${number}px`;
  reactions?: MessageEmojiReaction[];
}>(function MessagePrefix(props) {
  const { boxSize = '8px', reactions } = props;

  const {
    user: { user_id },
  } = useStaticAppStore();

  const list = useMemo(
    () => sortReactions(reactions, user_id)?.slice(0, 4),
    [reactions, user_id]
  );

  return list?.map((item) => (
    <Box
      key={item.emoji_id}
      borderRadius="full"
      px="1"
      py="0.5"
      display="flex"
      alignItems="center"
      gap="0.5"
      bg={item.isActive ? 'brand.50' : 'brand.10'}
      mr="0.5"
    >
      <Text fontSize="xs" flexShrink={0}>
        {formatEmojiId(item.emoji_id)}
      </Text>
      <EmojiAvatarGroup
        user_id={user_id}
        showMini={true}
        user_ids={item.reaction_log.map((log) => log.user_id)}
        boxSize={boxSize}
        maxDisplay={4}
        showCount={true}
      />
    </Box>
  ));
});

// Attach sub-components
Emoji.Quick = Quick;
Emoji.Panel = Panel;
Emoji.DetailDialog = DetailDialog;
Emoji.Item = Item;
Emoji.Avatar = MessageEmojiAvatar;
Emoji.AvatarGroup = EmojiAvatarGroup;
Emoji.MessageMeta = MessageMeta;
Emoji.MessagePrefix = MessagePrefix;

export { Emoji };
export type { DetailDialogRef };
